<template>
  <ion-page>
    <!-- Оверлей плеера — вне ion-content, чтобы fixed был относительно viewport -->
    <div class="video-overlay" v-show="showVideoPlayer" @click="exitVideoPlayer">
      <div
        ref="videoContainerRef"
        :class="[
          'video-container',
          { 'video-container--pseudo-fullscreen': isVideoContainerFullscreen && usePseudoFullscreen },
          { 'video-container--native-chrome': usePseudoFullscreen },
        ]"
        @click.stop
      >
        <div
          class="video-overlay-buttons"
          :class="{ 'video-overlay-buttons--hidden': usePseudoFullscreen && !videoChromeVisible }"
          @click.stop="bumpVideoChromeHideTimer">
          <button type="button" class="close-button" @click="exitVideoPlayer" aria-label="Закрыть">
            <ion-icon :icon="close"></ion-icon>
          </button>
          <button type="button" class="fullscreen-toggle-button" @click.stop="toggleVideoFullscreen" :aria-label="isVideoContainerFullscreen ? 'Выйти из полноэкранного режима' : 'Полный экран'">
            <ion-icon :icon="isVideoContainerFullscreen ? contractOutline : expandOutline"></ion-icon>
          </button>
          <button type="button" class="video-speed-button" @click.stop="cyclePlaybackRate" :aria-label="'Скорость ' + videoPlaybackRate + 'x'">
            <span class="video-speed-label">{{ videoPlaybackRate }}x</span>
          </button>
          <button type="button" class="video-mirror-button" @click.stop="toggleVideoMirror" :aria-label="videoMirror ? 'Выключить зеркало' : 'Зеркальное отображение'">
            <ion-icon :icon="swapHorizontalOutline"></ion-icon>
          </button>
        </div>
        <div class="video-player-wrap">
          <video
            ref="videoPlayer"
            :class="{ 'video-mirror': videoMirror }"
            oncontextmenu="return false;"
            preload="auto"
            autoPictureInPicture="true"
            playsinline
            webkit-playsinline
            x-webkit-airplay="allow"
            :controls="!useCustomVideoControls"
            crossorigin="anonymous"
            @loadedmetadata="onVideoLoaded"
            @canplay="onVideoCanplay"
            @timeupdate="onTimeUpdate"
            @waiting="onVideoWaiting"
            @playing="onVideoPlaying"
            @pause="onVideoPaused"
            @ended="onVideoEnded"
            @error="onVideoError">
            <source :src="trackPath" :type="trackPath.endsWith('m3u8')
              ? 'application/x-mpegURL'
              : 'video/mp4'
              " />
          </video>
          <!-- Натив: когда верхняя панель скрыта — тап по области видео снова её показывает -->
          <button
            v-if="usePseudoFullscreen && !videoChromeVisible"
            type="button"
            class="video-chrome-hitlayer"
            aria-label="Показать панель управления"
            @click.stop="showVideoChromeFromVideoTap"
          />
          <!-- Удержание левого края ~0.38 с — временно 2× (как в современных плеерах) -->
          <div
            v-show="showVideoPlayer"
            :class="['video-hold-2x-zone', useCustomVideoControls ? 'video-hold-2x-zone--web' : 'video-hold-2x-zone--native']"
            aria-hidden="true"
            @pointerdown.prevent.stop="onEducationHold2xPointerDown($event)"
            @pointerup.stop="onEducationHold2xPointerUp($event)"
            @pointercancel.stop="onEducationHold2xPointerCancel($event)"
          />
          <div v-show="educationHold2xHint" class="education-hold-2x-hint" aria-hidden="true">
            <ion-icon :icon="playForward" class="education-hold-2x-hint-icon" />
            <span class="education-hold-2x-badge">2×</span>
            <ion-icon :icon="playForward" class="education-hold-2x-hint-icon" />
          </div>
          <div v-show="videoLoading" class="video-player-loader">
            <ion-spinner name="bubbles" class="video-player-loader-spinner"></ion-spinner>
            <p class="video-player-loader-text">Подготовка воспроизведения...</p>
          </div>
          <!-- Обратный отсчёт по центру плеера (кнопка — нативная, чтобы избежать ошибки Vue/Ionic при быстром v-if) -->
          <div v-if="nextVideoCountdown >= 0" class="next-video-countdown-overlay" @click.stop="cancelNextVideo">
            <p class="next-video-countdown-overlay-text">Следующее видео через {{ nextVideoCountdown }}</p>
            <button type="button" class="next-video-countdown-cancel-btn">Отмена</button>
          </div>
          <!-- Кастомные контролы в стиле приложения (веб) -->
          <div v-if="useCustomVideoControls" class="video-controls-bar">
            <button type="button" class="video-controls-play" @click.stop="toggleVideoPlayPause" :aria-label="isVideoPlaying ? 'Пауза' : 'Воспроизведение'">
              <ion-icon :key="isVideoPlaying ? 'pause' : 'play'" :icon="isVideoPlaying ? pause : play"></ion-icon>
            </button>
            <div class="video-controls-progress-wrap" @click.stop="onVideoProgressClick">
              <div class="video-controls-progress-track">
                <div class="video-controls-progress-fill" :style="{ width: videoProgressPercent + '%' }"></div>
              </div>
            </div>
            <span class="video-controls-time">{{ formatVideoControlTime(videoControlCurrentTime) }} / {{ formatVideoControlTime(videoControlDuration) }}</span>
          </div>
        </div>
        <!-- Горизонтальная лента видео раздела под плеером -->
        <div v-if="currentVideoList.length" class="player-strip-wrap">
          <div class="player-strip-scroll" ref="playerStripRef">
            <button
              v-for="video in currentVideoList"
              :key="video.id"
              type="button"
              class="player-strip-item"
              :class="{ 'player-strip-item--active': video.id === selectedId }"
              @click="selectVideo(video.id)">
              <span class="player-strip-title">{{ formatVideoTitle(video.name) }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <PageHeaderWithMenu :title="pageTitleText" :scrolled="headerScrolled" />
    <ion-content class="main-background skills-content" :fullscreen="true" @ionScroll="onContentScroll">
      <!-- Лоадер при первой загрузке (пока нет разделов) -->
      <div v-if="skillsListLoading && visibleSections.length === 0" class="skills-loading-state skills-loading-initial">
        <ion-spinner name="bubbles" class="skills-loading-spinner"></ion-spinner>
        <p class="skills-loading-text">Загрузка...</p>
      </div>
      <!-- Раздел тренингов: горизонтальный селектор -->
      <div v-show="visibleSections.length > 0" class="section-select-wrap">
        <div class="section-chips-scroll" ref="sectionScrollRef">
          <button
            v-for="s in visibleSections"
            :key="s.folderKey"
            type="button"
            class="section-chip"
            :class="{ 'section-chip--active': selectedSectionKey === s.folderKey }"
            @click="selectedSectionKey = s.folderKey">
            {{ s.name }}
          </button>
        </div>
      </div>

      <!-- Вкладка: Видео | Обучающие материалы -->
      <div v-show="selectedSectionKey" class="education-tab-wrap">
        <ion-segment :value="educationTab" @ionChange="onEducationTabChange($event)" mode="ios" class="liga-glass-segment education-tab-segment">
          <ion-segment-button mode="ios" value="video">
            <ion-label>Видео</ion-label>
          </ion-segment-button>
          <ion-segment-button mode="ios" value="materials">
            <ion-label>Обучающие материалы</ion-label>
          </ion-segment-button>
        </ion-segment>
      </div>

      <!-- Video List -->
      <div v-show="selectedSectionKey && educationTab === 'video'" class="video-list-wrapper">
        <ion-refresher slot="fixed" @ionRefresh="reloadSkills($event)">
          <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
        </ion-refresher>

        <div v-if="skillsListLoading" class="skills-loading-state">
          <ion-spinner name="bubbles" class="skills-loading-spinner"></ion-spinner>
          <p class="skills-loading-text">Загрузка списка...</p>
        </div>
        <template v-else>
        <div v-if="currentVideoList.length" class="video-grid">
          <div 
            v-for="video in currentVideoList" 
            :key="video.id" 
            class="video-card"
            @click="selectVideo(video.id)">
            <div class="video-thumbnail">
              <div class="play-overlay">
                <div class="play-button" v-if="video.id !== selectedId || !videoLoading">
                  <ion-icon :icon="play"></ion-icon>
                </div>
                <ion-spinner v-else name="bubbles" class="loading-spinner"></ion-spinner>
              </div>
              <div class="thumbnail-gradient"></div>
              <img src="@/theme/liga-logo-white.png" alt="liga-logo" class="video-logo" />
              <div v-if="getVideoWatchPercent(video.id) != null" class="video-watch-progress-bar">
                <div class="video-watch-progress-fill" :style="{ width: `${getVideoWatchPercent(video.id)}%` }"></div>
                <span class="video-watch-progress-pct">{{ getVideoWatchPercent(video.id) }}%</span>
              </div>
              <div v-if="educationOfflineService.isMobile()" class="video-offline-badge" @click.stop>
                <ion-button v-show="!isVideoOffline(video.id) && !isVideoDownloading(video.id)" fill="clear" class="video-offline-btn" @click="downloadVideo(video)" title="Сохранить для оффлайн">
                  <ion-icon :icon="cloudDownloadOutline"></ion-icon>
                </ion-button>
                <ion-button v-show="isVideoDownloading(video.id)" fill="clear" class="video-offline-btn" @click="stopVideoDownload(video)" title="Остановить загрузку">
                  <ion-icon :icon="stopCircle"></ion-icon>
                </ion-button>
                <ion-button v-show="isVideoOffline(video.id) && !isVideoDownloading(video.id)" fill="clear" class="video-offline-btn video-offline-done" @click="removeVideo(video)" title="Удалить оффлайн">
                  <ion-icon :icon="trash"></ion-icon>
                </ion-button>
              </div>
              <div v-if="isVideoDownloading(video.id)" class="single-video-progress-bar">
                <div class="single-video-progress-track">
                  <div class="single-video-progress-fill" :style="{ width: `${singleVideoProgress.get(video.id)?.percentage ?? 0}%` }"></div>
                </div>
                <span class="single-video-progress-pct">{{ singleVideoProgress.get(video.id)?.percentage ?? 0 }}%</span>
              </div>
            </div>
            <div class="video-info">
              <h3 class="video-title">{{ formatVideoTitle(video.name) }}</h3>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <ion-icon :icon="filmOutline" class="empty-icon"></ion-icon>
          <p>Нет доступных видео</p>
        </div>
        </template>
      </div>

      <!-- Обучающие материалы (PDF) -->
      <div v-show="selectedSectionKey && educationTab === 'materials'" class="materials-list-wrapper">
        <ion-refresher slot="fixed" @ionRefresh="reloadSkills($event)">
          <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
        </ion-refresher>
        <div v-if="skillsListLoading" class="skills-loading-state">
          <ion-spinner name="bubbles" class="skills-loading-spinner"></ion-spinner>
          <p class="skills-loading-text">Загрузка материалов...</p>
        </div>
        <template v-else>
        <div v-if="currentPdfList.length" class="materials-list">
          <div
            v-for="pdf in currentPdfList"
            :key="pdf.id"
            class="material-card"
            @click="openPdfOrDownload(pdf)">
            <div class="material-icon-wrap">
              <ion-icon :icon="documentTextOutline" class="material-icon"></ion-icon>
            </div>
            <div class="material-info">
              <h3 class="material-title">{{ pdf.displayName }}</h3>
              <p class="material-meta">{{ pdf.sectionName || pdf.sectionFolderKey }}</p>
            </div>
            <ion-button
              fill="clear"
              size="small"
              :class="['material-download-btn', { 'material-download-btn--disabled': !isPdfDownloadAllowed(pdf) }]"
              @click.stop="downloadPdf(pdf)"
              :title="isPdfDownloadAllowed(pdf) ? 'Скачать PDF' : 'Скачивание недоступно'">
              <ion-icon slot="icon-only" :icon="cloudDownloadOutline"></ion-icon>
            </ion-button>
          </div>
        </div>
        <div v-else class="empty-state">
          <ion-icon :icon="documentOutline" class="empty-icon"></ion-icon>
          <p>Нет обучающих материалов</p>
        </div>
        </template>
      </div>

    </ion-content>

    <!-- Просмотр PDF — вне ion-content, overlay поверх всего; закрытие по кнопке или клику по затемнению -->
    <!-- Прогресс скачивания PDF на мобильном -->
    <div v-if="pdfDownloading" class="pdf-download-overlay">
      <div class="pdf-download-card">
        <p class="pdf-download-title">Скачивание PDF</p>
        <ion-progress-bar :value="pdfDownloadProgress / 100" type="determinate"></ion-progress-bar>
        <p class="pdf-download-percent">{{ pdfDownloadProgress }}%</p>
      </div>
    </div>

    <div v-show="showPdfViewer" class="pdf-viewer-overlay" @contextmenu.prevent @click="closePdfViewer">
      <div class="pdf-viewer-container" @click.stop @contextmenu.prevent>
        <button type="button" class="pdf-viewer-close" @click="closePdfViewer" aria-label="Закрыть">
          <ion-icon :icon="close"></ion-icon>
        </button>
        <template v-if="pdfViewerUrl">
          <!-- На Android (и iOS) iframe с data:application/pdf даёт белый экран — рендерим через PDF.js -->
          <div v-if="usePdfJsViewer" class="pdf-viewer-pdfjs">
            <VuePdfEmbed
              :source="pdfViewerUrl"
              annotation-layer
              text-layer
            />
          </div>
          <iframe
            v-else
            :src="pdfViewerUrl"
            class="pdf-viewer-iframe"
            title="Просмотр PDF"
            @contextmenu.prevent>
          </iframe>
        </template>
        <ion-spinner v-else name="bubbles" class="pdf-viewer-spinner"></ion-spinner>
      </div>
    </div>
  </ion-page>
</template>

<script lang="ts">
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonProgressBar,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  toastController
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { RmxAudioPlayer } from 'capacitor-plugin-playlist';
import { close, contractOutline, cloudDownloadOutline, documentOutline, documentTextOutline, expandOutline, filmOutline, pause, play, playForward, stopCircle, swapHorizontalOutline, trash } from "ionicons/icons";
import { defineComponent, inject, ref } from 'vue';
import { useRouter } from 'vue-router';

import { EducationSection } from '@/model/education-section.model';
import { IEducationPdfMaterial } from '@/model/education-pdf-material.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { DEFAULT_MEDIA_CATEGORY, MediaCategoryKey, mediaCategoryLabel, normalizeMediaCategory } from '@/model/media-category.model';
import AccountService from "@/services/account.service";
import AdminService from "@/services/admin.service";
import EducationOfflineService from "@/services/education-offline.service";
import EducationProgressService from "@/services/education-progress.service";
import VuePdfEmbed from 'vue-pdf-embed';
import 'vue-pdf-embed/src/styles/annotation-layer.css';
import 'vue-pdf-embed/src/styles/text-layer.css';

interface VideoPlayerMethods {
  requestFullscreen?: () => Promise<void>;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  msRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => void;
}

/** Unified video item for list (Google Drive or server). */
interface EducationVideoItem {
  id: string;
  name: string;
  source: 'google' | 'education';
}

export default defineComponent({
  name: 'VideoEducationPage',
  props: {
    mediaCategory: { type: String, default: DEFAULT_MEDIA_CATEGORY },
    pageTitle: { type: String, default: '' },
  },
  components: {
    IonButton,
    IonContent,
    IonIcon,
    IonLabel,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    PageHeaderWithMenu,
    VuePdfEmbed,
  },
  setup() {
    const globalContext = inject("globalContext") as GlobalContext;
    const audioPlayer = inject("audioPlayer") as RmxAudioPlayer;
    const router = useRouter();
    
    const videoPlayer = ref<HTMLVideoElement & VideoPlayerMethods>();
    const videoContainerRef = ref<HTMLElement | null>(null);
    const sectionScrollRef = ref<HTMLElement | null>(null);
    const playerStripRef = ref<HTMLElement | null>(null);
    
    return {
      globalContext,
      audioPlayer,
      router,
      videoPlayer,
      videoContainerRef,
      sectionScrollRef,
      playerStripRef,
      play,
      pause,
      playForward,
      close,
      contractOutline,
      expandOutline,
      swapHorizontalOutline,
      cloudDownloadOutline,
      documentOutline,
      documentTextOutline,
      filmOutline,
      stopCircle,
      trash,
    };
  },
  data() {
    return {
      accountService: new AccountService(),
      adminService: new AdminService(),
      educationOfflineService: new EducationOfflineService(),
      educationProgressService: new EducationProgressService(),
      hasAcademia: false,
      hasCoachGP: false,
      educationSections: [] as Array<{ name: string; folderKey: string }>,
      sectionVideos: {} as Record<string, EducationVideoItem[]>,
      selectedSectionKey: '',
      selectedId: '',
      headerScrolled: false,
      videoLoading: false,
      trackPath: '',
      token: '',
      showVideoPlayer: false,
      isVideoPlaying: false,
      offlineVideos: new Map<string, boolean>(),
      downloadingVideos: new Map<string, boolean>(),
      singleVideoProgress: new Map<string, { percentage: number; downloaded: number }>(),
      lastProgressSavedAt: 0,
      nextVideoCountdown: -1,
      nextVideoTimeoutId: null as ReturnType<typeof setTimeout> | null,
      educationTab: 'video' as 'video' | 'materials',
      sectionPdfs: {} as Record<string, IEducationPdfMaterial[]>,
      showPdfViewer: false,
      pdfViewerUrl: '',
      pdfDownloadProgress: 0,
      pdfDownloading: false,
      pdfDownloadPath: '',
      videoBlobUrlToRevoke: '' as string,
      pendingSeekPosition: null as number | null,
      skillsListLoading: false,
      videoProgress: {} as Record<string, { position: number; duration?: number }>,
      isVideoContainerFullscreen: false,
      videoControlCurrentTime: 0,
      videoControlDuration: 0,
      videoPlaybackRate: 1,
      educationHold2xTimer: null as ReturnType<typeof setTimeout> | null,
      educationHold2xPointerId: null as number | null,
      educationHold2xActive: false,
      educationHold2xHint: false,
      videoMirror: false,
      /** iOS/Android: верхняя панель (закрыть, полный экран…) — автоскрытие, повторно по тапу по видео */
      videoChromeVisible: true,
      videoChromeHideTimer: null as ReturnType<typeof setTimeout> | null,
    };
  },
  watch: {
    selectedSectionKey() {
      this.$nextTick(() => {
        this.scrollActiveChipIntoView();
        this.checkVideoOfflineStatus();
        this.loadVideoProgressForSection();
      });
    },
    currentVideoList() {
      this.checkVideoOfflineStatus();
      this.loadVideoProgressForSection();
    },
    showVideoPlayer(visible) {
      if (!visible) {
        this.clearVideoChromeHideTimer();
      } else if (this.usePseudoFullscreen) {
        (this as { videoChromeVisible: boolean }).videoChromeVisible = true;
        this.$nextTick(() => this.scheduleVideoChromeAutoHide());
      }
      if (this.usePseudoFullscreen && visible && typeof window !== 'undefined') {
        const isLandscape = window.innerWidth > window.innerHeight;
        (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = isLandscape;
      }
    },
    mediaCategory() {
      this.selectedSectionKey = '';
      this.reloadSkills();
    },
  },
  computed: {
    mediaCategoryKey(): MediaCategoryKey {
      return normalizeMediaCategory(this.mediaCategory);
    },
    pageTitleText(): string {
      return this.pageTitle || mediaCategoryLabel(this.mediaCategoryKey);
    },
    visibleSections(): Array<{ name: string; folderKey: string }> {
      return this.educationSections;
    },
    currentVideoList(): EducationVideoItem[] {
      return this.sectionVideos[this.selectedSectionKey] || [];
    },
    currentPdfList(): IEducationPdfMaterial[] {
      return this.sectionPdfs[this.selectedSectionKey] || [];
    },
    /** На iOS/Android используем псевдо-полноэкран (растягивание контейнера), т.к. Fullscreen API на iOS работает только для video, а не для div. */
    usePseudoFullscreen(): boolean {
      const platform = Capacitor.getPlatform();
      return platform === 'ios' || platform === 'android';
    },
    /** На вебе показываем кастомные контролы (прогресс и кнопки) в стиле приложения. */
    useCustomVideoControls(): boolean {
      return Capacitor.getPlatform() === 'web';
    },
    videoProgressPercent(): number {
      const d = this.videoControlDuration;
      const c = this.videoControlCurrentTime;
      if (!d || d <= 0) return 0;
      return Math.min(100, (c / d) * 100);
    },
    usePdfJsViewer(): boolean {
      return Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
    },
  },
  async ionViewWillEnter() {
    try {
      const response = await Preferences.get({ key: "jwt" });
      this.token = response.value || '';
      await this.getVideoList();
    } catch (error) {
      console.error('Failed to get token:', error);
      this.showErrorToast('Ошибка загрузки данных');
    }
  },
  ionViewDidLeave() {
    this.exitVideoPlayer();
    this.closePdfViewer();
  },
  mounted() {
    this.setupVideoListeners();
    window.addEventListener('resize', this.handleWindowResize);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  },
  beforeUnmount() {
    this.clearVideoChromeHideTimer();
    this.exitVideoPlayer();
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  },
  methods: {
    async showErrorToast(message: string) {
      const isLong = message.length > 60;
      const toast = await toastController.create({
        message,
        duration: isLong ? 6000 : 3000,
        position: 'top',
        color: 'danger',
      });
      await toast.present();
    },
    /** Имя раздела при отсутствии сохранённого маппинга с сервера — показываем ключ как есть. */
    offlineSectionDisplayName(folderKey: string): string {
      if (folderKey == null || typeof folderKey !== 'string') return '—';
      const s = folderKey.trim();
      return s || '—';
    },
    async loadOfflineVideoList() {
      if (!this.educationOfflineService.isMobile()) {
        this.showErrorToast('Оффлайн режим: видео недоступны');
        return;
      }
      (this as { skillsListLoading: boolean }).skillsListLoading = true;
      try {
        const keys = await this.educationOfflineService.getOfflineSectionKeys();
        if (keys.length === 0) {
          this.educationSections = [];
          this.sectionVideos = {};
          this.sectionPdfs = {};
          this.showErrorToast('Нет загруженных видео для оффлайн');
          return;
        }
        const savedNames = await this.educationOfflineService.getSectionNames();
        const nameFor = (key: string) => {
          if (key == null || typeof key !== 'string') return '—';
          if (savedNames[key]) return savedNames[key];
          const lower = key.toLowerCase();
          const found = Object.keys(savedNames).find((k) => k.toLowerCase() === lower);
          const name = found ? savedNames[found] : this.offlineSectionDisplayName(key);
          return name || key || '—';
        };
        this.educationSections = keys.map((folderKey) => ({
          name: nameFor(folderKey),
          folderKey: folderKey ?? '',
        }));
        if (!keys.includes(this.selectedSectionKey)) {
          this.selectedSectionKey = keys[0] ?? '';
        }
        const sectionVideos: Record<string, EducationVideoItem[]> = {};
        const sectionPdfs: Record<string, IEducationPdfMaterial[]> = {};
        for (const key of keys) {
          try {
            const items = await this.educationOfflineService.getOfflineVideosInSection(key);
            sectionVideos[key] = items.map(({ id, name }) => ({
              id,
              name,
              source: id.startsWith('edu:') ? 'education' : 'google',
            }));
          } catch {
            sectionVideos[key] = [];
          }
          sectionPdfs[key] = [];
        }
        this.sectionVideos = sectionVideos;
        this.sectionPdfs = sectionPdfs;
      } catch {
        this.educationSections = [];
        this.sectionVideos = {};
        this.sectionPdfs = {};
        this.showErrorToast('Ошибка загрузки оффлайн-списка');
      } finally {
        (this as { skillsListLoading: boolean }).skillsListLoading = false;
      }
    },
    async getVideoList() {
      if (this.globalContext.offlineMode) {
        await this.loadOfflineVideoList();
        return;
      }
      (this as { skillsListLoading: boolean }).skillsListLoading = true;
      try {
        this.hasAcademia = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_ACADEMIA");
        this.hasCoachGP = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_COACH_GP");
        let hasFullAccess = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_MANAGER");
        if (!hasFullAccess) hasFullAccess = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_ADMIN");
        if (!hasFullAccess) hasFullAccess = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_RELEASE_FULL");

        let sections: Array<{ name: string; folderKey: string }> = [];
        try {
          const apiSections = await this.adminService.getEducationSectionsForCurrentUser(this.mediaCategoryKey);
          if (apiSections && apiSections.length > 0) {
            sections = apiSections.map((s: EducationSection) => ({
              name: s.folderKey === 'Ted' ? 'Лекции' : s.name,
              folderKey: s.folderKey,
            }));
            if (this.mediaCategoryKey === DEFAULT_MEDIA_CATEGORY && !sections.some((s) => s.folderKey === 'Ted')) {
              sections = [{ name: 'Лекции', folderKey: 'Ted' }, ...sections];
            }
          }
        } catch (_) {
          // API недоступен — используем fallback
        }
        if (sections.length === 0 && this.mediaCategoryKey === DEFAULT_MEDIA_CATEGORY) {
          sections = [{ name: 'Лекции', folderKey: 'Ted' }];
          if (this.hasAcademia || hasFullAccess) sections.push({ name: 'Академия', folderKey: 'Academia' });
          if (this.hasCoachGP || hasFullAccess) sections.push({ name: 'Тренер ГП', folderKey: 'CoachGP' });
        }

        this.educationSections = sections;
        if (sections.length > 0 && !this.selectedSectionKey) {
          this.selectedSectionKey = sections[0].folderKey;
        }
        try {
          await this.educationOfflineService.saveSectionNames(sections);
        } catch {
          // сохранение имён для оффлайна не критично
        }

        const sectionVideos: Record<string, EducationVideoItem[]> = {};
        const sectionPdfs: Record<string, IEducationPdfMaterial[]> = {};
        await Promise.all(
          sections.map(async (s) => {
            const [videos, pdfs] = await Promise.all([
              this.fetchVideos(s.folderKey),
              this.adminService.getPublicEducationPdfs(s.folderKey, this.mediaCategoryKey),
            ]);
            sectionVideos[s.folderKey] = videos;
            sectionPdfs[s.folderKey] = Array.isArray(pdfs) ? pdfs : [];
          })
        );
        this.sectionVideos = sectionVideos;
        this.sectionPdfs = sectionPdfs;
        this.loadVideoProgressForSection();
        await this.applyShowcaseDeepLinkFromRoute();
      } catch (error) {
        console.error('Error loading videos:', error);
        this.showErrorToast('Ошибка загрузки списка видео');
      } finally {
        (this as { skillsListLoading: boolean }).skillsListLoading = false;
      }
    },
    /** Переход с витрины: /skills?section=… или &openVideo=numericId */
    async applyShowcaseDeepLinkFromRoute() {
      const q = this.$route.query;
      const section = typeof q.section === 'string' ? q.section.trim() : '';
      const openVideo = typeof q.openVideo === 'string' ? q.openVideo.trim() : '';
      if (!section) return;
      const inSections = this.educationSections.some((s) => s.folderKey === section);
      if (!inSections) return;
      this.selectedSectionKey = section;
      await this.$nextTick();
      if (!openVideo) {
        if (this.$route.query.section) {
          await this.$router.replace({ path: this.$route.path, query: {} });
        }
        return;
      }
      const eduId = `edu:${openVideo}`;
      const videos = this.sectionVideos[section];
      if (!videos?.some((v) => v.id === eduId)) return;
      try {
        await this.selectVideo(eduId);
      } catch {
        /* selectVideo уже показал toast */
      } finally {
        if (this.$route.query.section || this.$route.query.openVideo) {
          await this.$router.replace({ path: this.$route.path, query: {} });
        }
      }
    },
    async fetchVideos(folder: string): Promise<EducationVideoItem[]> {
      const items: EducationVideoItem[] = [];
      const googleDriveSections = ['Ted', 'Academia'];
      if (this.mediaCategoryKey === DEFAULT_MEDIA_CATEGORY && googleDriveSections.includes(folder)) {
        try {
          const apiBase = process.env.VUE_APP_API_EXT_URL || process.env.VUE_APP_API_URL || '';
          const response = await CapacitorHttp.get({
            url: `${apiBase}api/google/media/${folder}`,
            headers: { Authorization: `Bearer ${this.token}` },
            readTimeout: 60000,
          });
          if (response.status === 404 || response.status >= 400) {
            if (response.status !== 404) {
              console.warn(`Error ${response.status} fetching Google Drive videos for folder: ${folder}`);
            }
          } else {
            const data = (response.data || []) as Array<{ id: string; name: string }>;
            if (Array.isArray(data)) {
              data.forEach((f) => items.push({ id: f.id, name: f.name || '', source: 'google' }));
            }
          }
        } catch (error: unknown) {
          const status = (error as { status?: number })?.status;
          if (status !== 404) {
            console.warn(`Error fetching Google Drive videos for ${folder}:`, error);
          }
        }
      }
      // 2. Серверные видео (education_videos)
      try {
        const serverVideos = await this.adminService.getPublicEducationVideos(folder, this.mediaCategoryKey);
        if (Array.isArray(serverVideos)) {
          serverVideos.forEach((v) => {
            const name = (v.displayName || '').trim() || (v.fileName || '').split('.')[0];
            items.push({ id: `edu:${v.id}`, name: name || 'Видео', source: 'education' });
          });
        }
      } catch (e) {
        console.warn(`Error fetching server videos for ${folder}:`, e);
      }
      // Сортировка по имени: 01, 02… затем А, Б…
      items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true }));
      return items;
    },
    async selectVideo(id: string) {
      this.cancelNextVideo();
      (this as { pendingSeekPosition: number | null }).pendingSeekPosition = null;
      if (this.selectedId === id) {
        this.exitVideoPlayer();
        return;
      }
      this.selectedId = id;
      this.videoLoading = true;

      try {
        this.trackPath = await this.getVideoUrl(id);
        this.showVideoPlayer = true;
        await this.$nextTick();
        if (this.videoPlayer) {
          this.videoPlayer.src = this.trackPath;
          this.videoPlayer.load();
        }
        this.$nextTick(() => this.scrollPlayerStripToActive());
      } catch (error) {
        console.error('Error loading video:', error);
        const msg = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios'
          ? `Ошибка загрузки видео: ${(error as Error)?.message ?? String(error)}`
          : 'Ошибка загрузки видео';
        this.showErrorToast(msg);
        this.videoLoading = false;
        this.selectedId = '';
      }
    },
    async getVideoUrl(id: string): Promise<string> {
      if (!id || typeof id !== 'string') throw new Error('Invalid video id');
      if (this.educationOfflineService.isMobile() && this.selectedSectionKey) {
        const offlinePath = await this.educationOfflineService.getOfflinePath(this.selectedSectionKey, id);
        if (offlinePath) return offlinePath;
      }
      if (id.startsWith('edu:')) {
        const raw = id.slice(4).trim();
        const videoId = Number(raw);
        if (!Number.isInteger(videoId) || videoId < 1) throw new Error('Invalid education video id');
        return this.adminService.getEducationVideoStreamUrl(videoId);
      }
      try {
        const apiBase = process.env.VUE_APP_API_EXT_URL || process.env.VUE_APP_API_URL || '';
        await CapacitorHttp.get({
          url: `${apiBase}api/google/video/storage/${id}`,
          headers: { Authorization: `Bearer ${this.token}` },
          readTimeout: 60000,
        });
        return this.adminService.getGoogleVideoStreamUrl(id);
      } catch (error: unknown) {
        console.error('Error getting video URL:', error);
        throw error;
      }
    },
    async checkVideoOfflineStatus() {
      if (!this.educationOfflineService.isMobile() || !this.selectedSectionKey) return;
      this.offlineVideos.clear();
      for (const video of this.currentVideoList) {
        const ok = await this.educationOfflineService.isOffline(this.selectedSectionKey, video.id);
        this.offlineVideos.set(video.id, ok);
      }
    },
    isVideoOffline(videoId: string): boolean {
      return this.offlineVideos.get(videoId) === true;
    },
    isVideoDownloading(videoId: string): boolean {
      return this.downloadingVideos.get(videoId) === true;
    },
    async downloadVideo(video: EducationVideoItem) {
      if (!video) return;
      this.downloadingVideos.set(video.id, true);
      this.singleVideoProgress.set(video.id, { percentage: 0, downloaded: 0 });
      try {
        await this.educationOfflineService.downloadVideo(
          video.id,
          this.selectedSectionKey,
          (downloadedMB: number, totalMB: number) => {
            const pct = totalMB > 0 ? Math.round((100 * downloadedMB) / totalMB) : 0;
            this.singleVideoProgress.set(video.id, { percentage: pct, downloaded: downloadedMB });
            this.singleVideoProgress = new Map(this.singleVideoProgress);
          }
        );
        this.offlineVideos.set(video.id, true);
        const toast = await toastController.create({
          message: 'Видео сохранено для оффлайн',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        if ((e as Error).message !== 'Download cancelled') {
          console.error('downloadVideo', e);
          const msg = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios'
            ? `Ошибка загрузки видео: ${(e as Error)?.message ?? String(e)}`
            : 'Ошибка загрузки видео';
          this.showErrorToast(msg);
        }
      } finally {
        this.downloadingVideos.delete(video.id);
        this.singleVideoProgress.delete(video.id);
        await this.checkVideoOfflineStatus();
      }
    },
    async stopVideoDownload(video: EducationVideoItem) {
      this.educationOfflineService.cancelDownload(video.id);
    },
    async removeVideo(video: EducationVideoItem) {
      if (!video) return;
      await this.educationOfflineService.removeVideo(this.selectedSectionKey, video.id);
      this.offlineVideos.delete(video.id);
      const toast = await toastController.create({
        message: 'Оффлайн копия удалена',
        duration: 2000,
        position: 'top',
        color: 'medium',
      });
      await toast.present();
    },
    setupVideoListeners() {
      if (!this.videoPlayer) return;

      const video = this.videoPlayer;

      video.addEventListener('enterpictureinpicture', () => {
        this.isVideoPlaying = true;
      });

      video.addEventListener('leavepictureinpicture', () => {
        this.isVideoPlaying = false;
      });
    },
    async onVideoLoaded() {
      this.videoLoading = false;
      const duration = this.videoPlayer?.duration;
      const durationValid = duration != null && Number.isFinite(duration) && duration > 0;
      let pos: number | null = null;
      try {
        const server = await this.adminService.getEducationVideoProgress(this.selectedId);
        if (server?.lastPositionSeconds != null) {
          if (!durationValid || server.lastPositionSeconds < duration - 10) {
            pos = server.lastPositionSeconds;
          }
        }
      } catch {
        // ignore
      }
      if (pos == null) {
        pos = this.educationProgressService.getPosition(this.selectedId);
      }
      // На мобильном duration часто NaN до canplay; ставим позицию, если она не в последних 10 с (когда duration известен)
      if (pos != null && pos >= 0 && this.videoPlayer) {
        if (!durationValid || pos < duration - 10) {
          this.videoPlayer.currentTime = pos;
          (this as { pendingSeekPosition: number | null }).pendingSeekPosition = pos;
        }
      }
      this.lastProgressSavedAt = this.videoPlayer?.currentTime ?? 0;
      if (this.useCustomVideoControls && this.videoPlayer) {
        (this as { videoControlDuration: number }).videoControlDuration = this.videoPlayer.duration ?? 0;
        (this as { videoControlCurrentTime: number }).videoControlCurrentTime = this.videoPlayer.currentTime ?? 0;
      }
      if (this.videoPlayer) {
        this.videoPlayer.playbackRate = this.videoPlaybackRate;
      }
      this.playVideo();
    },
    onVideoCanplay() {
      const pending = (this as { pendingSeekPosition: number | null }).pendingSeekPosition;
      if (this.videoPlayer && this.useCustomVideoControls) {
        const dur = this.videoPlayer.duration;
        if (Number.isFinite(dur) && dur > 0) (this as { videoControlDuration: number }).videoControlDuration = dur;
        (this as { videoControlCurrentTime: number }).videoControlCurrentTime = this.videoPlayer.currentTime ?? 0;
      }
      if (pending == null || !this.videoPlayer) return;
      const current = this.videoPlayer.currentTime;
      if (Math.abs(current - pending) > 2) {
        this.videoPlayer.currentTime = pending;
        this.lastProgressSavedAt = pending;
      }
      (this as { pendingSeekPosition: number | null }).pendingSeekPosition = null;
    },
    onVideoWaiting() {
      this.videoLoading = true;
    },
    onVideoPlaying() {
      this.videoLoading = false;
      this.isVideoPlaying = true;
      this.pauseAudioIfPlaying();
    },
    onTimeUpdate() {
      if (!this.videoPlayer) return;
      const now = this.videoPlayer.currentTime;
      const dur = this.videoPlayer.duration;
      if (this.useCustomVideoControls) {
        (this as { videoControlCurrentTime: number }).videoControlCurrentTime = now;
        if (Number.isFinite(dur) && dur > 0) (this as { videoControlDuration: number }).videoControlDuration = dur;
      }
      if (!this.selectedId || !this.isVideoPlaying) return;
      if (now - this.lastProgressSavedAt >= 10) {
        this.educationProgressService.savePosition(this.selectedId, now);
        this.lastProgressSavedAt = now;
        this.saveProgressToServer(this.selectedId, now, dur);
      }
    },
    onVideoPaused() {
      this.isVideoPlaying = false;
      if (this.videoPlayer && this.selectedId) {
        const pos = this.videoPlayer.currentTime;
        this.educationProgressService.savePosition(this.selectedId, pos);
        this.saveProgressToServer(this.selectedId, pos, this.videoPlayer.duration);
      }
    },
    onVideoEnded() {
      this.isVideoPlaying = false;
      if (this.selectedId) this.educationProgressService.clearPosition(this.selectedId);
      this.scheduleNextVideo();
    },
    scheduleNextVideo() {
      if (this.nextVideoTimeoutId != null) clearTimeout(this.nextVideoTimeoutId);
      this.nextVideoTimeoutId = null;
      const list = this.currentVideoList;
      const idx = list.findIndex((v) => v.id === this.selectedId);
      if (idx < 0 || idx >= list.length - 1) return;
      const nextId = list[idx + 1].id;
      this.nextVideoCountdown = 2;
      const tick = () => {
        this.nextVideoCountdown -= 1;
        if (this.nextVideoCountdown <= 0) {
          this.nextVideoCountdown = -1;
          this.nextVideoTimeoutId = null;
          this.selectVideo(nextId);
          return;
        }
        this.nextVideoTimeoutId = setTimeout(tick, 1000);
      };
      this.nextVideoTimeoutId = setTimeout(tick, 1000);
    },
    cancelNextVideo() {
      if (this.nextVideoTimeoutId != null) clearTimeout(this.nextVideoTimeoutId);
      this.nextVideoTimeoutId = null;
      this.nextVideoCountdown = -1;
    },
    toggleVideoPlayPause() {
      if (!this.videoPlayer) return;
      if (this.videoPlayer.paused) {
        this.videoPlayer.play().catch(() => {});
      } else {
        this.videoPlayer.pause();
      }
    },
    onVideoProgressClick(e: MouseEvent) {
      if (!this.videoPlayer || !this.useCustomVideoControls) return;
      const wrap = (e.currentTarget as HTMLElement).querySelector('.video-controls-progress-track');
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const dur = this.videoPlayer.duration;
      if (Number.isFinite(dur) && dur > 0) {
        this.videoPlayer.currentTime = pct * dur;
        (this as { videoControlCurrentTime: number }).videoControlCurrentTime = this.videoPlayer.currentTime;
      }
    },
    formatVideoControlTime(seconds: number): string {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
    clearVideoChromeHideTimer() {
      if (this.videoChromeHideTimer != null) {
        clearTimeout(this.videoChromeHideTimer);
        (this as { videoChromeHideTimer: ReturnType<typeof setTimeout> | null }).videoChromeHideTimer = null;
      }
    },
    scheduleVideoChromeAutoHide() {
      if (!this.usePseudoFullscreen || !this.showVideoPlayer) return;
      this.clearVideoChromeHideTimer();
      (this as { videoChromeHideTimer: ReturnType<typeof setTimeout> | null }).videoChromeHideTimer = setTimeout(() => {
        (this as { videoChromeHideTimer: ReturnType<typeof setTimeout> | null }).videoChromeHideTimer = null;
        (this as { videoChromeVisible: boolean }).videoChromeVisible = false;
      }, 4000);
    },
    bumpVideoChromeHideTimer() {
      if (!this.usePseudoFullscreen) return;
      (this as { videoChromeVisible: boolean }).videoChromeVisible = true;
      this.scheduleVideoChromeAutoHide();
    },
    showVideoChromeFromVideoTap() {
      this.bumpVideoChromeHideTimer();
    },
    unlockEducationVideoOrientation() {
      if (!this.usePseudoFullscreen) return;
      try {
        ScreenOrientation.unlock();
      } catch {
        /* ignore */
      }
    },
    cyclePlaybackRate() {
      this.bumpVideoChromeHideTimer();
      const rates = [1, 1.5, 2];
      const idx = rates.indexOf(this.videoPlaybackRate);
      const next = rates[(idx + 1) % rates.length];
      (this as { videoPlaybackRate: number }).videoPlaybackRate = next;
      if (this.videoPlayer) {
        this.videoPlayer.playbackRate = next;
      }
    },
    clearEducationHold2x() {
      if (this.educationHold2xTimer != null) {
        clearTimeout(this.educationHold2xTimer);
        this.educationHold2xTimer = null;
      }
      if (this.educationHold2xActive && this.videoPlayer) {
        this.videoPlayer.playbackRate = this.videoPlaybackRate;
      }
      this.educationHold2xActive = false;
      this.educationHold2xHint = false;
      this.educationHold2xPointerId = null;
    },
    onEducationHold2xPointerDown(e: PointerEvent) {
      if (!this.videoPlayer || e.button !== 0) return;
      this.bumpVideoChromeHideTimer();
      this.educationHold2xPointerId = e.pointerId;
      this.educationHold2xActive = false;
      if (this.educationHold2xTimer != null) {
        clearTimeout(this.educationHold2xTimer);
        this.educationHold2xTimer = null;
      }
      this.educationHold2xTimer = setTimeout(() => {
        this.educationHold2xTimer = null;
        if (!this.videoPlayer || this.educationHold2xPointerId !== e.pointerId) return;
        this.educationHold2xActive = true;
        this.videoPlayer.playbackRate = 2;
        this.educationHold2xHint = true;
      }, 380);
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    onEducationHold2xPointerUp(e: PointerEvent) {
      if (this.educationHold2xPointerId !== e.pointerId) return;
      const el = e.currentTarget as HTMLElement;
      if (this.educationHold2xTimer != null) {
        clearTimeout(this.educationHold2xTimer);
        this.educationHold2xTimer = null;
      }
      const wasHold2x = this.educationHold2xActive;
      if (wasHold2x && this.videoPlayer) {
        this.videoPlayer.playbackRate = this.videoPlaybackRate;
      }
      this.educationHold2xActive = false;
      this.educationHold2xHint = false;
      this.educationHold2xPointerId = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    onEducationHold2xPointerCancel(e: PointerEvent) {
      if (this.educationHold2xPointerId !== e.pointerId) return;
      const el = e.currentTarget as HTMLElement;
      if (this.educationHold2xTimer != null) {
        clearTimeout(this.educationHold2xTimer);
        this.educationHold2xTimer = null;
      }
      if (this.educationHold2xActive && this.videoPlayer) {
        this.videoPlayer.playbackRate = this.videoPlaybackRate;
      }
      this.educationHold2xActive = false;
      this.educationHold2xHint = false;
      this.educationHold2xPointerId = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    toggleVideoMirror() {
      this.bumpVideoChromeHideTimer();
      (this as { videoMirror: boolean }).videoMirror = !this.videoMirror;
    },
    async onVideoError(event: Event) {
      const videoElement = event.target as HTMLVideoElement;
      const error = videoElement.error;
      const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';

      if (error && error.code === 4 && isNative && this.trackPath && this.trackPath.startsWith('http')) {
        try {
          this.videoLoading = true;
          const res = await fetch(this.trackPath);
          if (!res.ok) throw new Error(`${res.status}`);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          if ((this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke) {
            URL.revokeObjectURL((this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke);
          }
          (this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke = blobUrl;
          this.trackPath = blobUrl;
          videoElement.src = blobUrl;
          videoElement.load();
          this.videoLoading = false;
          return;
        } catch (e) {
          console.error('Video blob fallback failed:', e);
          this.videoLoading = false;
        }
      }

      if (error) {
        const codeLabels: Record<number, string> = {
          1: 'MEDIA_ERR_ABORTED',
          2: 'MEDIA_ERR_NETWORK',
          3: 'MEDIA_ERR_DECODE',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
        };
        console.error('Video error details:', { code: error.code, message: error.message, mediaError: error });
        const code4Hint = isNative && error.code === 4
          ? ' Попробуйте скачать видео для офлайн.'
          : '';
        const detail = isNative
          ? ` (код ${error.code} ${codeLabels[error.code] ?? ''}: ${error.message || 'без сообщения'})${code4Hint}`
          : '';
        this.videoLoading = false;
        this.showErrorToast(isNative ? `Ошибка воспроизведения видео${detail}` : 'Ошибка воспроизведения видео');
      } else {
        this.videoLoading = false;
        this.showErrorToast('Ошибка воспроизведения видео');
      }
      this.exitVideoPlayer();
    },
    async playVideo(retryCount = 0) {
      if (!this.videoPlayer || retryCount > 2) return;

      try {
        // For iOS, we need to trigger play in response to user gesture
        await this.videoPlayer.play().catch(e => {
          // If direct play fails, try entering fullscreen first (iOS workaround)
          if (this.videoPlayer?.webkitEnterFullscreen) {
            this.videoPlayer.webkitEnterFullscreen();
          }
          throw e;
        });
      } catch (error) {
        console.error('Error playing video:', error);
        const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
        const msg = isNative
          ? `Не удалось воспроизвести видео: ${(error as Error)?.message ?? String(error)}`
          : 'Не удалось воспроизвести видео';
        this.showErrorToast(msg);
        this.exitVideoPlayer();
      }
    },
    pauseAudioIfPlaying() {
      if (this.audioPlayer && this.audioPlayer.hasLoaded) {
        this.audioPlayer.pause();
      }
    },
    exitVideoPlayer() {
      this.clearEducationHold2x();
      this.clearVideoChromeHideTimer();
      (this as { videoChromeVisible: boolean }).videoChromeVisible = true;
      this.unlockEducationVideoOrientation();
      this.cancelNextVideo();
      (this as { pendingSeekPosition: number | null }).pendingSeekPosition = null;
      if ((this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke) {
        URL.revokeObjectURL((this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke);
        (this as { videoBlobUrlToRevoke: string }).videoBlobUrlToRevoke = '';
      }
      if (this.videoPlayer) {
        if (this.selectedId) {
          const pos = this.videoPlayer.currentTime;
          this.educationProgressService.savePosition(this.selectedId, pos);
          this.saveProgressToServer(this.selectedId, pos, this.videoPlayer.duration);
        }
        this.videoPlayer.pause();
        if (document.fullscreenElement) {
          document.exitFullscreen?.().catch(() => {});
        } else if (this.videoPlayer.webkitExitFullscreen) {
          this.videoPlayer.webkitExitFullscreen();
        }
      }
      this.trackPath = '';
      this.selectedId = '';
      this.showVideoPlayer = false;
      this.videoLoading = false;
      this.isVideoPlaying = false;
      (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = false;
      (this as { videoControlCurrentTime: number }).videoControlCurrentTime = 0;
      (this as { videoControlDuration: number }).videoControlDuration = 0;
    },
    onFullscreenChange() {
      if (this.usePseudoFullscreen) return;
      const el = document.fullscreenElement ?? (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement;
      const container = this.videoContainerRef as HTMLElement | null;
      if (el === this.videoPlayer) {
        document.exitFullscreen?.().catch(() => {});
        (document as unknown as { webkitExitFullscreen?: () => void }).webkitExitFullscreen?.();
        if (container?.requestFullscreen) {
          container.requestFullscreen().catch(() => {});
        } else if ((container as unknown as { webkitRequestFullscreen?: () => void })?.webkitRequestFullscreen) {
          (container as unknown as { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
        }
        return;
      }
      (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = el === container;
    },
    async toggleVideoFullscreen() {
      if (this.usePseudoFullscreen) {
        const next = !this.isVideoContainerFullscreen;
        (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = next;
        try {
          if (next) {
            await ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.LANDSCAPE);
          } else {
            ScreenOrientation.unlock();
          }
        } catch (e) {
          console.warn('Education video: screen orientation', e);
        }
        this.bumpVideoChromeHideTimer();
        return;
      }
      const container = this.videoContainerRef as HTMLElement | null;
      if (!container) return;
      const el = document.fullscreenElement ?? (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement;
      if (el) {
        document.exitFullscreen?.().catch(() => {});
        (document as unknown as { webkitExitFullscreen?: () => void }).webkitExitFullscreen?.();
        (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = false;
      } else {
        try {
          if (container.requestFullscreen) {
            await container.requestFullscreen();
          } else if ((container as unknown as { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen) {
            (container as unknown as { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
          }
          (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = true;
        } catch {
          /* requestFullscreen can reject if cancelled by exitFullscreen() — ignore */
        }
      }
    },
    onEducationTabChange(e: CustomEvent) {
      const value = e.detail?.value as string | undefined;
      if (value === 'video' || value === 'materials') {
        this.educationTab = value;
      }
    },
    reloadSkills(event?: CustomEvent) {
      this.getVideoList().finally(() => {
        if (event?.detail) {
          event.detail.complete();
        }
      });
    },
    formatVideoTitle(name: string): string {
      return name ? name.split('.')[0] : '';
    },
    handleWindowResize() {
      if (this.usePseudoFullscreen && this.showVideoPlayer) {
        const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;
        const wasFs = this.isVideoContainerFullscreen;
        (this as { isVideoContainerFullscreen: boolean }).isVideoContainerFullscreen = isLandscape;
        if (wasFs && !isLandscape) {
          try {
            ScreenOrientation.unlock();
          } catch {
            /* ignore */
          }
        }
      }
    },
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    scrollActiveChipIntoView() {
      const scrollEl = this.sectionScrollRef as HTMLElement | null;
      if (!scrollEl) return;
      const active = scrollEl.querySelector('.section-chip--active');
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },
    scrollPlayerStripToActive() {
      const strip = this.playerStripRef as HTMLElement | null;
      if (!strip) return;
      const active = strip.querySelector('.player-strip-item--active');
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },
    saveProgressToServer(videoId: string, positionSeconds: number, durationSeconds?: number) {
      if (!Number.isFinite(positionSeconds)) return;
      if (positionSeconds > 0 && (durationSeconds == null || Number.isFinite(durationSeconds))) {
        const next = { ...this.videoProgress, [videoId]: { position: positionSeconds, duration: durationSeconds } };
        (this as { videoProgress: Record<string, { position: number; duration?: number }> }).videoProgress = next;
      }
      this.adminService.putEducationVideoProgress(videoId, positionSeconds, Number.isFinite(durationSeconds) ? durationSeconds : undefined).catch(() => {
        // ignore
      });
    },
    isPdfDownloadAllowed(pdf: IEducationPdfMaterial): boolean {
      return pdf.allowDownload === true || (pdf as unknown as Record<string, unknown>)['allow_download'] === true;
    },
    /** Процент просмотра по сохранённому прогрессу; null если не смотрели или 0% — не показываем на карточке. */
    getVideoWatchPercent(videoId: string): number | null {
      const p = this.videoProgress[videoId];
      if (!p || p.position <= 0) return null;
      const duration = p.duration;
      if (duration == null || duration <= 0) return null;
      const percent = Math.round((100 * p.position) / duration);
      if (percent <= 0) return null;
      return Math.min(100, percent);
    },
    /** Подгружает прогресс просмотра для видео текущего раздела (в фоне, не блокирует отрисовку). */
    loadVideoProgressForSection() {
      if (this.globalContext.offlineMode || !this.currentVideoList.length) return;
      const list = this.currentVideoList;
      Promise.all(
        list.map(async (video) => {
          try {
            const res = await this.adminService.getEducationVideoProgress(video.id);
            if (res?.lastPositionSeconds != null && res.lastPositionSeconds > 0)
              return { id: video.id, position: res.lastPositionSeconds, duration: res.durationSeconds };
          } catch {
            // ignore
          }
          return null;
        })
      ).then((results) => {
        const next: Record<string, { position: number; duration?: number }> = {};
        results.forEach((r) => {
          if (r) next[r.id] = { position: r.position, duration: r.duration };
        });
        (this as { videoProgress: Record<string, { position: number; duration?: number }> }).videoProgress = { ...this.videoProgress, ...next };
      });
    },
    /** По клику на строку — всегда открываем встроенный просмотр (VuePdfEmbed/PDF.js на iOS и Android). Скачивание — только по кнопке. */
    openPdfOrDownload(pdf: IEducationPdfMaterial) {
      this.openPdfViewer(pdf);
    },
    async openPdfViewer(pdf: IEducationPdfMaterial) {
      try {
        (this as { pdfViewerUrl: string }).pdfViewerUrl = '';
        (this as { showPdfViewer: boolean }).showPdfViewer = true;
        const blob = await this.adminService.getEducationPdfViewBlob(pdf.id);
        const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
        if (isNative) {
          const base64 = await this.blobToBase64(blob);
          (this as { pdfViewerUrl: string }).pdfViewerUrl = `data:application/pdf;base64,${base64}`;
        } else {
          const url = URL.createObjectURL(blob);
          (this as { pdfViewerUrl: string }).pdfViewerUrl = url;
        }
      } catch (e) {
        console.error('openPdfViewer', e);
        (this as { showPdfViewer: boolean }).showPdfViewer = false;
        this.showErrorToast('Ошибка загрузки материала');
      }
    },
    blobToBase64(blob: Blob): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1] || '';
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    closePdfViewer() {
      const url = (this as { pdfViewerUrl: string }).pdfViewerUrl;
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
      (this as { pdfViewerUrl: string }).pdfViewerUrl = '';
      (this as { showPdfViewer: boolean }).showPdfViewer = false;
    },
    async downloadPdf(pdf: IEducationPdfMaterial) {
      if (!this.isPdfDownloadAllowed(pdf)) {
        this.showErrorToast('Скачивание этого материала отключено');
        return;
      }
      const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
      if (isNative) {
        await this.downloadPdfNative(pdf);
        return;
      }
      try {
        const blob = await this.adminService.getEducationPdfDownloadBlob(pdf.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (pdf.displayName || 'material').replace(/[<>:"/\\|?*]/g, '_') + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        const toast = await toastController.create({
          message: 'Файл сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('downloadPdf', e);
        const msg = (e as Error).message === 'Download not allowed' ? 'Скачивание этого материала недоступно' : 'Ошибка скачивания';
        this.showErrorToast(msg);
      }
    },
    async downloadPdfNative(pdf: IEducationPdfMaterial) {
      const API = (() => {
        const v = process.env.VUE_APP_API_URL || '';
        return v && !v.endsWith('/') ? v + '/' : v;
      })();
      const token = await Preferences.get({ key: 'jwt' }).then((r) => r.value || '');
      const url = `${API}api/education/pdf/${pdf.id}/download`;
      (this as { pdfDownloading: boolean }).pdfDownloading = true;
      (this as { pdfDownloadProgress: number }).pdfDownloadProgress = 0;
      (this as { pdfDownloadPath: string }).pdfDownloadPath = '';
      try {
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(res.status === 403 ? 'Download not allowed' : `HTTP ${res.status}`);
        const contentLength = res.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        const reader = res.body?.getReader();
        if (!reader) throw new Error('No body');
        const chunks: Uint8Array[] = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          if (total > 0) {
            (this as { pdfDownloadProgress: number }).pdfDownloadProgress = Math.min(99, Math.round((100 * received) / total));
          }
        }
        const blob = new Blob(chunks as BlobPart[], { type: 'application/pdf' });
        const base64 = await this.blobToBase64(blob);
        const fileName = (pdf.displayName || 'material').replace(/[<>:"/\\|?*]/g, '_') + '.pdf';
        const dir = Directory.Documents;
        const relativePath = `LigaTeam/${fileName}`;
        await Filesystem.writeFile({
          path: relativePath,
          data: base64,
          directory: dir,
          recursive: true,
        });
        const { uri } = await Filesystem.getUri({ path: relativePath, directory: dir });
        (this as { pdfDownloadProgress: number }).pdfDownloadProgress = 100;
        (this as { pdfDownloadPath: string }).pdfDownloadPath = uri;
        const toast = await toastController.create({
          message: `PDF сохранён: Документы/LigaTeam/${fileName}`,
          duration: 4000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('downloadPdfNative', e);
        const msg = (e as Error).message === 'Download not allowed' ? 'Скачивание этого материала недоступно' : 'Ошибка скачивания';
        this.showErrorToast(msg);
      } finally {
        (this as { pdfDownloading: boolean }).pdfDownloading = false;
        (this as { pdfDownloadProgress: number }).pdfDownloadProgress = 0;
      }
    },
  },
});
</script>

<style scoped>
.section-select-wrap {
  margin: 0 16px 16px;
}

.section-chips-scroll {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.section-chips-scroll::-webkit-scrollbar {
  display: none;
}

.section-chip {
  flex-shrink: 0;
  padding: 10px 18px;
  border-radius: 24px;
  border: 1px solid var(--liga-float-glass-border);
  background: var(--liga-float-glass-bg);
  color: var(--liga-chrome-text);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
}

.section-chip:hover {
  background: linear-gradient(135deg, #e85d04 0%, #dc2f02 100%);
  border-color: transparent;
  color: var(--liga-chrome-text);
}

.section-chip--active {
  background: linear-gradient(135deg, #e85d04 0%, #dc2f02 100%);
  border-color: transparent;
  color: var(--liga-chrome-text);
  box-shadow: 0 2px 8px rgba(232, 93, 4, 0.4);
}

.education-tab-wrap {
  margin: 0 16px 12px;
}

.education-tab-segment {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  max-width: 100%;
}

.materials-list-wrapper {
  padding: 0 16px 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(180deg, rgba(42, 42, 42, 0.95) 0%, rgba(30, 30, 30, 0.98) 100%);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}

.material-card:hover {
  transform: translateY(-2px);
  border-color: rgba(232, 93, 4, 0.3);
}

.material-download-btn {
  margin-left: auto;
  --padding-start: 8px;
  --padding-end: 8px;
  color: var(--ion-color-orange);
}

.material-download-btn--disabled {
  opacity: 0.5;
}

.material-icon-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: rgba(232, 93, 4, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-icon {
  font-size: 28px;
  color: var(--ion-color-orange);
}

.material-info {
  flex: 1;
  min-width: 0;
}

.material-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--liga-chrome-text);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.material-meta {
  margin: 0;
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
}

/* Просмотр PDF: blob URL, контекстное меню отключено */
.pdf-download-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.pdf-download-card {
  background: var(--liga-float-glass-bg-strong);
  padding: 24px;
  border-radius: 16px;
  min-width: 260px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
.pdf-download-title {
  color: var(--liga-chrome-text);
  margin: 0 0 12px;
  font-size: 1rem;
}
.pdf-download-card ion-progress-bar {
  margin-bottom: 8px;
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.2);
}
.pdf-download-percent {
  color: var(--liga-chrome-text);
  margin: 0;
  font-size: 0.95rem;
  text-align: center;
}

.pdf-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
}

.pdf-viewer-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 900px;
  max-height: 100vh;
  padding: 50px 16px 16px;
  z-index: 1001;
  pointer-events: auto;
}

.pdf-viewer-close {
  position: absolute;
  top: max(10px, env(safe-area-inset-top));
  right: max(16px, env(safe-area-inset-right));
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.85);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: var(--liga-chrome-text);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
  cursor: pointer;
  pointer-events: auto;
  flex-shrink: 0;
}

.pdf-viewer-close ion-icon {
  font-size: 24px;
}

.pdf-viewer-pdfjs {
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding: 8px;
  background: #fff;
  border-radius: 8px;
}

.pdf-viewer-pdfjs .vue-pdf-embed {
  margin-bottom: 16px;
}

.pdf-viewer-pdfjs .vue-pdf-embed__page {
  margin: 0 auto 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pdf-viewer-iframe {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
  border-radius: 8px;
  background: #fff;
}

.pdf-viewer-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--ion-color-orange);
}

/* Отступ снизу, чтобы список не перекрывался нижним меню */
.skills-content {
  --padding-bottom: calc(100px + env(safe-area-inset-bottom));
  --padding-start: 16px;
  --padding-end: 16px;
}

.video-list-wrapper {
  padding-bottom: 24px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.video-card {
  background: var(--liga-float-glass-bg-strong);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--liga-float-glass-border);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  cursor: pointer;
  box-shadow: var(--liga-float-glass-shadow);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  color: var(--liga-chrome-text);
}

.video-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(235, 89, 82, 0.2);
  border-color: rgba(235, 89, 82, 0.3);
}

.video-card:active {
  transform: translateY(-2px);
}

.video-thumbnail {
  position: relative;
  padding-top: 56.25%; /* 16:9 */
  background: linear-gradient(135deg, #1a1a1a 0%, #252525 100%);
}

.thumbnail-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  pointer-events: none;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.2s ease;
}

.video-card:hover .play-overlay {
  background: rgba(0, 0, 0, 0.35);
}

.play-button {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(235, 89, 82, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(235, 89, 82, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.video-card:hover .play-button {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(235, 89, 82, 0.5);
}

.play-button ion-icon {
  font-size: 28px;
  color: var(--liga-chrome-text);
  margin-left: 4px; /* визуальный центр для треугольника play */
}

.play-overlay .loading-spinner {
  color: var(--liga-chrome-text);
  width: 40px;
  height: 40px;
}

.video-logo {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 22%;
  max-width: 48px;
  opacity: 0.9;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
}

.video-offline-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 4px;
}

.video-offline-btn {
  --padding-start: 6px;
  --padding-end: 6px;
  margin: 0;
  min-height: 32px;
  color: var(--liga-chrome-text);
}

.video-offline-btn ion-icon {
  font-size: 20px;
}

.video-offline-done ion-icon {
  color: #4caf50;
}

.single-video-progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  padding: 0 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 12px 12px;
}

.single-video-progress-track {
  flex: 1;
  min-width: 0;
  height: 5px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.single-video-progress-fill {
  height: 100%;
  background: var(--ion-color-orange);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.single-video-progress-pct {
  font-size: 11px;
  font-weight: 600;
  color: var(--liga-chrome-text);
  flex-shrink: 0;
}

.video-info {
  padding: 14px 16px;
}

.video-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--liga-chrome-text);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.skills-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: var(--liga-chrome-text-muted);
}
.skills-loading-initial {
  min-height: 40vh;
}
.skills-loading-spinner {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
}
.skills-loading-text {
  margin: 0;
  font-size: 0.95rem;
}

.video-watch-progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  padding: 0 8px;
  pointer-events: none;
  border-radius: 0 0 8px 8px;
}
.video-watch-progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: linear-gradient(90deg, #e85d04 0%, #dc2f02 100%);
  border-radius: 0 2px 0 0;
  max-width: 100%;
  transition: width 0.25s ease;
}
.video-watch-progress-pct {
  position: relative;
  z-index: 1;
  font-size: 11px;
  font-weight: 600;
  color: var(--liga-chrome-text);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.empty-state {
  text-align: center;
  padding: 60px 24px;
  color: var(--liga-chrome-text-muted);
}

.empty-state .empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Video Overlay Styles */
.video-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container {
  width: 100%;
  max-width: 800px;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 20px;
  overflow: hidden;
}

/* Псевдо-полноэкран на мобильных: контейнер на весь экран без Fullscreen API (iOS не поддерживает его для div). */
.video-container--pseudo-fullscreen {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  padding: max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left));
}

/* iOS/Android: панель действий в потоке над видео (не absolute поверх кадра), с отступом от выреза */
.video-container--native-chrome {
  padding: max(12px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left));
}

.video-container--native-chrome .video-overlay-buttons {
  position: relative;
  top: auto;
  right: auto;
  left: auto;
  width: 100%;
  justify-content: flex-end;
  margin: 0 0 12px;
  padding-top: 2px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition:
    opacity 0.25s ease,
    max-height 0.28s ease,
    margin-bottom 0.28s ease,
    padding-top 0.28s ease,
    padding-bottom 0.28s ease;
}

.video-container--native-chrome .video-overlay-buttons--hidden {
  opacity: 0;
  max-height: 0;
  min-height: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.video-container--native-chrome .video-player-wrap {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container--native-chrome .video-player-wrap video {
  width: 100%;
  max-height: 100%;
  height: auto;
  object-fit: contain;
}

.video-container--native-chrome.video-container--pseudo-fullscreen .video-player-wrap {
  flex: 1;
  min-height: 0;
}

.video-container--native-chrome.video-container--pseudo-fullscreen .video-player-wrap video {
  max-height: 100%;
}

.video-chrome-hitlayer {
  position: absolute;
  inset: 0;
  z-index: 4;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.video-hold-2x-zone {
  position: absolute;
  left: 0;
  top: 0;
  width: 18%;
  z-index: 6;
  touch-action: none;
  user-select: none;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
}

/* Не перекрывать нижнюю панель (веб — кастомные контролы; натив — полоса системного плеера) */
.video-hold-2x-zone--web {
  bottom: 52px;
}

.video-hold-2x-zone--native {
  bottom: 72px;
}

.education-hold-2x-hint {
  position: absolute;
  left: 10%;
  top: 42%;
  transform: translate(-50%, -50%);
  z-index: 8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  pointer-events: none;
  font-family: var(--ion-font-family);
}

.education-hold-2x-hint-icon {
  font-size: 1.4rem;
  color: var(--liga-chrome-text);
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.9));
}

.education-hold-2x-badge {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--ion-color-orange);
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.9);
  line-height: 1;
}

.video-player-wrap {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.video-container video,
.video-player-wrap video {
  width: 100%;
  border-radius: 8px;
  background: #000;
  display: block;
}

.video-player-wrap video.video-mirror {
  transform: scaleX(-1);
}

.video-player-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--liga-surface);
  border-radius: 8px;
  pointer-events: none;
}

.video-player-loader-spinner {
  width: 56px;
  height: 56px;
  color: var(--ion-color-orange);
  margin-bottom: 12px;
}

.video-player-loader-text {
  margin: 0;
  color: var(--liga-chrome-text);
  font-size: 0.95rem;
  font-family: var(--ion-font-family);
  font-weight: 500;
}

/* Обратный отсчёт по центру плеера */
.next-video-countdown-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 8px;
  z-index: 10;
}

.next-video-countdown-overlay-text {
  margin: 0;
  color: var(--liga-chrome-text);
  font-size: 1.1rem;
  font-family: var(--ion-font-family);
  font-weight: 600;
}

.next-video-countdown-cancel-btn {
  padding: 8px 16px;
  font-size: 0.875rem;
  font-family: var(--ion-font-family);
  font-weight: 600;
  color: var(--ion-color-orange);
  background: transparent;
  border: 1px solid var(--ion-color-orange);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.next-video-countdown-cancel-btn:hover {
  background: rgba(235, 89, 82, 0.1);
}
.next-video-countdown-cancel-btn:active {
  background: rgba(235, 89, 82, 0.2);
}

/* Кастомные контролы видео в стиле приложения (веб) — по мотивам MDN Video player styling */
.video-controls-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--liga-surface);
  border-radius: 0 0 8px 8px;
  z-index: 5;
}

.video-controls-play {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--liga-chrome-text);
  transition: background 0.2s, color 0.2s;
}

.video-controls-play:hover {
  background: rgba(255, 255, 255, 0.15);
  color: var(--ion-color-orange);
}

.video-controls-play ion-icon {
  font-size: 22px;
}

.video-controls-progress-wrap {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  padding: 6px 0;
}

.video-controls-progress-track {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  overflow: hidden;
}

.video-controls-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e85d04 0%, #dc2f02 100%);
  border-radius: 3px;
  transition: width 0.15s ease;
}

.video-controls-time {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--liga-chrome-text);
  font-family: var(--ion-font-family);
}

.player-strip-wrap {
  width: 100%;
  margin-top: 12px;
  flex-shrink: 0;
  overflow: hidden;
}

.player-strip-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.player-strip-item {
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: var(--liga-surface);
  color: var(--liga-chrome-text);
  font-size: 0.875rem;
  font-family: var(--ion-font-family);
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.player-strip-item:hover {
  background: rgba(60, 60, 60, 0.9);
  border-color: rgba(235, 89, 82, 0.5);
  color: var(--liga-chrome-text);
}

.player-strip-item--active {
  background: var(--ion-color-orange);
  border-color: var(--ion-color-orange);
  color: var(--liga-chrome-text);
}

.player-strip-title {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

.video-overlay-buttons {
  position: absolute;
  top: max(10px, env(safe-area-inset-top));
  right: max(10px, env(safe-area-inset-right));
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1001;
}

.close-button,
.fullscreen-toggle-button,
.video-speed-button,
.video-mirror-button {
  background: var(--liga-surface);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.15s;
}

.video-speed-button {
  min-width: 44px;
  width: auto;
  padding: 0 8px;
}

.video-speed-label {
  color: var(--liga-chrome-text);
  font-size: 0.8rem;
  font-weight: 600;
  font-family: var(--ion-font-family);
}

.close-button:hover,
.fullscreen-toggle-button:hover,
.video-speed-button:hover,
.video-mirror-button:hover {
  background: rgba(60, 60, 60, 0.95);
  border-color: rgba(255, 255, 255, 0.35);
}

.close-button:active,
.fullscreen-toggle-button:active,
.video-speed-button:active,
.video-mirror-button:active {
  background: rgba(235, 89, 82, 0.25);
  border-color: var(--ion-color-orange);
}

.close-button ion-icon,
.fullscreen-toggle-button ion-icon,
.video-mirror-button ion-icon {
  color: var(--liga-chrome-text);
  font-size: 22px;
}

.video-mirror-button:active ion-icon,
.close-button:active ion-icon,
.fullscreen-toggle-button:active ion-icon {
  color: var(--ion-color-orange);
}

.video-speed-button:active .video-speed-label {
  color: var(--ion-color-orange);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skills-content {
    --padding-bottom: calc(88px + env(safe-area-inset-bottom));
  }
  
  .video-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;
  }
  
  .play-button {
    width: 48px;
    height: 48px;
  }
  
  .play-button ion-icon {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .video-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1024px) {
  .skills-content {
    --padding-bottom: calc(108px + env(safe-area-inset-bottom));
  }
}

/* Плеер остаётся тёмным immersive-surface в любой теме */
.video-overlay,
.video-container,
.player-strip-wrap,
.pdf-viewer-overlay {
  --liga-chrome-text: #ffffff;
  --liga-chrome-text-muted: rgba(255, 255, 255, 0.72);
  --liga-surface: rgba(255, 255, 255, 0.1);
  --liga-float-glass-border: rgba(255, 255, 255, 0.22);
}
</style>